import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@contexts/AuthContext';
import { formatGivenName } from '@utils/stringUtils';
import { dashboardService } from '@services';
import PageLayout from '@components/layout/PageLayout';
import { AssessorMetrics, AssessorQuickActions } from '@components/features/dashboard';
import Button from '@components/common/Button';
import DashboardHeader from '@components/common/DashboardHeader';
import DashboardGrid from '@components/common/DashboardGrid';
import DashboardRow from '@components/common/DashboardRow';
import DashboardContainer from '@components/common/DashboardContainer';
import DashboardCard from '@components/common/DashboardCard';
import ErrorState from '@components/common/ErrorState';
import type { AssessorMetricsData, AssessorQuickActionsData } from '@app-types';

const REFRESH_INTERVAL_MS = 60000;

const AssessorDashboardPage: React.FC = () => {
  const { user } = useAuth();

  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError
  } = useQuery<AssessorMetricsData>({
    queryKey: ['assessor-metrics', user?.id],
    queryFn: () => dashboardService.getAssessorMetrics(),
    refetchInterval: REFRESH_INTERVAL_MS,
    enabled: !!user?.id
  });

  const {
    data: quickActionsData,
    isLoading: quickActionsLoading,
    error: quickActionsError
  } = useQuery<AssessorQuickActionsData>({
    queryKey: ['assessor-quick-actions', user?.id],
    queryFn: () => dashboardService.getAssessorQuickActions(),
    refetchInterval: REFRESH_INTERVAL_MS,
    enabled: !!user?.id
  });

  if (metricsError || quickActionsError) {
    return (
      <PageLayout>
        <ErrorState
          title="Dashboard Unavailable"
          message="Unable to load your dashboard data. Please try refreshing the page."
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <DashboardContainer data-test-id="assessor-dashboard">
        <DashboardHeader
          title={`Welcome back, ${user ? formatGivenName(user) : ''}!`}
          subtitle="Your meetings and assessment workload"
          role="Assessor Dashboard"
          lastUpdated={`Last updated: ${new Date().toLocaleTimeString()}`}
        />
        <DashboardGrid>
          <DashboardRow>
            <AssessorMetrics data={metricsData} isLoading={metricsLoading} userId={user?.id ?? 0} />
          </DashboardRow>
          <DashboardRow>
            <DashboardCard title="Quick Actions">
              <AssessorQuickActions data={quickActionsData} isLoading={quickActionsLoading} userId={user?.id ?? 0} />
            </DashboardCard>
          </DashboardRow>
        </DashboardGrid>
      </DashboardContainer>
    </PageLayout>
  );
};

export default AssessorDashboardPage;
